
export class ImageHelper {
    static readonly disallowedAutomateImageExtensions = ['tiff', 'bmp', 'svg'];
    static readonly IMAGE_EXTS = /\.(jpg|jpeg|png|gif|webp|svg|avif|bmp|tiff?|heic|heif)$/i;

    /**
     * Uses a url to fetch image data and provide access to the image's width through a callback
     * function.
     *
     * @param {string} url
     */
    static async getImageWidth(url: string): Promise<number> {
        const p = new Promise<number>(function(resolve, reject) {
            const img = new Image();
            img.onload = function() {
                resolve(img.width);
            };
            img.onerror = function() {
                reject();
            };
            img.src = url;
        });
        return p;
    }

    /**
     * Uses a url to fetch image data and provide access to the image's height through a callback
     * function.
     *
     * @param {string} url
     */
    static getImageHeight(url: string): Promise<number> {
        const p = new Promise<number>(function(resolve, reject) {
            const img = new Image();
            img.onload = function() {
                resolve(img.height);
            };
            img.onerror = function() {
                reject();
            };
            img.src = url;
        });
        return p;
    }

    /**
     * Uses a url to fetch image data and provide access to the image's dimensions through a callback
     * function.
     *
     * @param {string} url
     */
    static async getImageDimensions(url: string): Promise<number[]> {
        const p = new Promise<number[]>(function(resolve, reject) {
            const img = new Image();
            img.onload = function() {
                resolve([img.width, img.height]);
            };
            img.onerror = function() {
                reject();
            };
            img.src = url;
        });
        return p;
    }

    /**
     * Uses a url to fetch image file size in bytes.
     *
     * @param {string} url
     */
    static async getImageFileSize(url: string): Promise<number> {
        const p = await fetch(url)
            .then(res => {
                return res.ok ? res.blob() : null;
            })
            .then(blob => blob ? blob.size : null);
        return p;
    }

    static guessExtensionFromUrl(url: string): string | undefined {
        try {
            const pathname = new URL(url, window.location.href).pathname;
            const last = pathname.split('/').pop() || '';
            const dot = last.lastIndexOf('.');
            if (dot > -1) {
                return last.slice(dot + 1).toLowerCase();
            }
        } catch {
            // ignore invalid URLs
        }
        return undefined;
    }

    static async validateAutomateImage(url: string): Promise<boolean> {
        const [width, height] = await this.getImageDimensions(url);

        if (!width || !height) {
            return false;
        }

        if (width < 300 || height < 300) {
            return false;
        }
        if (width > 5000 || height > 5000) {
            return false;
        }

        // 4. Aspect ratio check
        const aspectRatio = width / height;
        if (aspectRatio < 0.5) {
            return false;
        } else if (aspectRatio > 2) {
            return false;
        }

        const extension = this.guessExtensionFromUrl(url);
        if (!extension || this.disallowedAutomateImageExtensions.includes(extension)) {
            return false;
        }

        return true;
    }

    static normalizeImageUrl(url: string): string {
        try {
            const parsed = new URL(url);
            // Ignore query params and hash
            return `${parsed.origin}${parsed.pathname}`;
        } catch {
        // Fallback for invalid URLs
            return url.split('?')[0];
        }
    }

    static getImageNameFromUrl (
        url: string,
        fallback = 'image'
    ): string {
        if (!url) {
            return `${fallback}.jpg`;
        };

        // data:image/*
        if (url.startsWith('data:image/')) {
            const ext = url.match(/^data:image\/([^;,]+)/i)?.[1] ?? 'png';
            return `${fallback}.${ext === 'jpeg' ? 'jpg' : ext}`;
        }

        try {
            const u = new URL(url.startsWith('//') ? `https:${url}` : url);

            const filename = decodeURIComponent(
                u.pathname.split('/').pop() ?? ''
            )
                .split('?')[0]
                .split('#')[0];

            if (this.IMAGE_EXTS.test(filename)) {
                return filename.replace(/\.jpeg$/i, '.jpg');
            }
        } catch {
            /* ignore invalid URL */
        }

        return `${fallback}.jpg`;
    }
}
